import React, { useState, useEffect,useRef } from "react";
import { faKey, faUser, faExclamationCircle, faCheckCircle, faPhone, faStore, faInfoCircle, faMapMarkedAlt, faCity, faMapPin, faBuilding, faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Button from "components/base/Button";
import { Alert, Col, Form, Row, Spinner, Container } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { SELLER_REGISTER_MUTATION } from "api/graphql/mutations/sellerRegisterMutation";
import { useAuth } from "providers/AuthProvider";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "components/custom/toastify-custom.css";
import "./SignUpForm.css";

const SignUpForm = ({ layout }: { layout: "simple" | "card" | "split" }) => {
  const { t } = useTranslation(); // Initialize translation hook
  // Password visibility states
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    shopTitle: "",
    contactNumber: "",
    description: "",
    firstname: "",
    lastname: "",
    company: "",
    street: "",
    city: "",
    postcode: "",
    region: "Tunis",
    region_id: 11,
    region_code: "TU",
    vat_id: "",
  });

  const [errors, setErrors] = useState<string[]>([]);
  const { setSeller, setIsAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Form validation states
  const [validated, setValidated] = useState(false);
  const [formValid, setFormValid] = useState(false);
  
  // Field-specific validation states
  const [fieldValidation, setFieldValidation] = useState({
    email: { isValid: true, message: "" },
    password: { 
      isValid: true, 
      message: "",
      hasUpperCase: false,
      hasLowerCase: false,
      hasNumber: false,
      hasSpecialChar: false,
      isLongEnough: false
    },
    confirmPassword: { isValid: true, message: "" },
    contactNumber: { isValid: true, message: "" },
    vat_id: { isValid: true, message: "" }
  });

  // Touched state to track which fields user has interacted with
  const [touched, setTouched] = useState({
    email: false,
    password: false,
    confirmPassword: false,
    contactNumber: false,
    vat_id: false,
    termsService: false
  });

  // Terms agreement state
  const [termsAgreed, setTermsAgreed] = useState(false);
  
  // Validation functions
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isValidPhone = (phone: string): boolean => {
    // Tunisian phone number format: 8 digits, can start with +216 or not
    const phoneRegex = /^(\+216)?[2-9]\d{7}$/;
    return phoneRegex.test(phone);
  };

  const isValidPassword = (password: string): boolean => {
    // Password must have at least one uppercase, one lowercase, one number, one special char, and be at least 6 chars long
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*()_+\-={}();':"\\|,.<>/?]/.test(password);
    const isLongEnough = password.length >= 6;

    // Update detailed password validation state
    setFieldValidation(prev => ({
      ...prev,
      password: {
        ...prev.password,
        hasUpperCase,
        hasLowerCase,
        hasNumber,
        hasSpecialChar,
        isLongEnough,
        isValid: hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar && isLongEnough
      }
    }));

    return hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar && isLongEnough;
  };

  // References for form inputs to scroll to errors
  const formRef = useRef<HTMLFormElement>(null);
  const fieldRefs = useRef<{[key: string]: React.RefObject<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>}>({
    firstname: React.createRef(),
    lastname: React.createRef(),
    email: React.createRef(),
    contactNumber: React.createRef(),
    password: React.createRef(),
    confirmPassword: React.createRef(),
    shopTitle: React.createRef(),
    company: React.createRef(),
    description: React.createRef(),
    street: React.createRef(),
    city: React.createRef(),
    postcode: React.createRef(),
    region: React.createRef(),
    vat_id: React.createRef(),
  });

  // Function to scroll to the first error field
  const scrollToFirstError = (fields: string[]) => {
    for (const field of fields) {
      const ref = fieldRefs.current[field];
      if (ref && ref.current) {
        ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        ref.current.focus();
        break;
      }
    }
  };

  // Use the mutation for seller registration
  const [sellerRegister, { loading, error: mutationError }] = useMutation(SELLER_REGISTER_MUTATION, {
    onCompleted: (data) => {
      if (data?.sellerRegister?.success) {
        const { seller, token } = data.sellerRegister;
        
        // Store auth data
        localStorage.setItem("authToken", token);
        localStorage.setItem("seller", JSON.stringify(seller));
        
        // Update auth context
        setSeller(seller);
        setIsAuthenticated(true);
        
        // Show success toast
        toast.success(t("registrationSuccessful") || "Registration successful!");
        
        // Redirect to home page after successful registration
        navigate("/"); 
      } else {
        // Show error message from API using toast
        const errorMessage = data?.sellerRegister?.message || t("unknownError");
        toast.error(errorMessage);
        setErrors([errorMessage]);
      }
    },
    onError: (error) => {
      const errorMessage = error?.message || t("registrationFailed");
      // Show error in toast
      toast.error(errorMessage);
      setErrors([errorMessage]);
    }
  });
  
  // Check password match and overall form validity
  useEffect(() => {
    const passwordsMatch = Boolean(formData.password && formData.password === formData.confirmPassword);
    const passwordValid = isValidPassword(formData.password);
    const emailValid = isValidEmail(formData.email);
    const phoneValid = isValidPhone(formData.contactNumber);
    
    const requiredFieldsFilled = Boolean(
      formData.email && 
      formData.password && 
      formData.confirmPassword && 
      formData.shopTitle && 
      formData.contactNumber && 
      formData.description && 
      formData.firstname && 
      formData.lastname && 
      formData.company && 
      formData.vat_id && 
      formData.street && 
      formData.city && 
      formData.postcode && 
      termsAgreed // Terms agreement is required
    );
      
    setFormValid(passwordsMatch && passwordValid && emailValid && phoneValid && requiredFieldsFilled);
  }, [formData, termsAgreed]);

  // Validate fields in real-time
  useEffect(() => {
    // Only validate if user has interacted with the field
    if (touched.email) {
      if (!formData.email) {
        setFieldValidation(prev => ({
          ...prev,
          email: { isValid: false, message: t("emailRequired") || "Email is required" }
        }));
      } else if (!isValidEmail(formData.email)) {
        setFieldValidation(prev => ({
          ...prev,
          email: { isValid: false, message: t("invalidEmail") || "Invalid email format" }
        }));
      } else {
        setFieldValidation(prev => ({
          ...prev,
          email: { isValid: true, message: "" }
        }));
      }
    }

    if (touched.contactNumber) {
      if (!formData.contactNumber) {
        setFieldValidation(prev => ({
          ...prev,
          contactNumber: { isValid: false, message: t("contactNumberRequired") || "Contact number is required" }
        }));
      } else if (!isValidPhone(formData.contactNumber)) {
        setFieldValidation(prev => ({
          ...prev,
          contactNumber: { isValid: false, message: t("invalidPhoneFormat") || "Phone must be 8 digits, starting with +216 (optional)" }
        }));
      } else {
        setFieldValidation(prev => ({
          ...prev,
          contactNumber: { isValid: true, message: "" }
        }));
      }
    }

    if (touched.confirmPassword) {
      if (formData.password !== formData.confirmPassword) {
        setFieldValidation(prev => ({
          ...prev,
          confirmPassword: { isValid: false, message: t("passwordsDoNotMatch") || "Passwords do not match" }
        }));
      } else {
        setFieldValidation(prev => ({
          ...prev,
          confirmPassword: { isValid: true, message: "" }
        }));
      }
    }
  }, [formData, touched, t]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;

    // Mark field as touched when user interacts with it
    if (name in touched) {
      setTouched(prev => ({
        ...prev,
        [name]: true
      }));
    }

    if (name === "shopTitle") {
      setFormData({
        ...formData,
        shopTitle: value,
        company: value,
      });
    } else if (name === "contactNumber") {
      // Only allow numeric characters for contact number
      const numericValue = value.replace(/[^0-9+]/g, '');
      setFormData({
        ...formData,
        [name]: numericValue,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }

    // Special validation for password
    if (name === "password") {
      isValidPassword(value);
    }
  };

  // Prevent non-numeric key presses in contact number field
  const handleContactKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow: backspace, delete, tab, escape, enter, plus sign, and arrow keys
    if (
      e.key === 'Backspace' || 
      e.key === 'Delete' || 
      e.key === 'Tab' || 
      e.key === 'Escape' || 
      e.key === 'Enter' || 
      e.key === '+' ||
      e.key === 'ArrowUp' || 
      e.key === 'ArrowDown' || 
      e.key === 'ArrowLeft' || 
      e.key === 'ArrowRight'
    ) {
      return;
    }

    // Ensure that it is a number and stop the keypress if not
    if (!/[0-9]/.test(e.key)) {
      e.preventDefault();
    }
  };

  // Handle field blur - mark as touched when user leaves a field
  const handleBlur = (field: string) => {
    if (field in touched) {
      setTouched(prev => ({
        ...prev,
        [field]: true
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Get the form element
    const form = e.currentTarget as HTMLFormElement;
    
    // Array to collect error fields for scrolling
    const errorFields: string[] = [];
    
    // Mark all fields as touched to trigger validation
    setTouched({
      email: true,
      password: true,
      confirmPassword: true,
      contactNumber: true,
      vat_id: true,
      termsService: true
    });
    
    // Force validation check
    setValidated(true);
    
    // Check form validity using Bootstrap's validation
    if (!form.checkValidity()) {
      e.stopPropagation();
      
      // Find all invalid fields
      const invalidElements = form.querySelectorAll(':invalid');
      invalidElements.forEach(el => {
        const field = el.getAttribute('name');
        if (field) errorFields.push(field);
      });
      
      if (errorFields.length > 0) {
        scrollToFirstError(errorFields);
        toast.error(t("Please Fill all The Required Fields") || "Please fix the errors in the form");
      }
      return;
    }
    
    // Check email validation
    if (!isValidEmail(formData.email)) {
      errorFields.push('email');
      toast.error(t("invalidEmail") || "Invalid email format");
      scrollToFirstError(errorFields);
      return;
    }
    
    // Check phone validation
    if (!isValidPhone(formData.contactNumber)) {
      errorFields.push('contactNumber');
      toast.error(t("invalidPhoneFormat") || "Invalid phone number format");
      scrollToFirstError(errorFields);
      return;
    }

    // Check password complexity
    if (!isValidPassword(formData.password)) {
      errorFields.push('password');
      toast.error(t("passwordDoesNotMeetCriteria") || "Password does not meet complexity requirements");
      scrollToFirstError(errorFields);
      return;
    }
    
    // Check if passwords match
    if (formData.password !== formData.confirmPassword) {
      errorFields.push('confirmPassword');
      toast.error(t("passwordsDoNotMatch") || "Passwords do not match");
      scrollToFirstError(errorFields);
      return;
    }
    
    // Check terms agreement
    if (!termsAgreed) {
      errorFields.push('termsService');
      toast.error(t("termsRequired") || "You must agree to the terms and privacy policy");
      scrollToFirstError(errorFields);
      return;
    }

    // Check if any required fields are empty
    const requiredFields: (keyof typeof formData)[] = [
      'email', 'password', 'confirmPassword', 'shopTitle', 
      'contactNumber', 'description', 'firstname', 'lastname', 
      'company', 'street', 'city', 'postcode', 'vat_id'
    ];
    
    const emptyFields = requiredFields.filter(field => {
      const value = formData[field];
      return !value || (typeof value === "string" && !value.trim());
    });
    
    if (emptyFields.length > 0) {
      // Add all empty fields to errorFields for scrolling
      emptyFields.forEach(field => errorFields.push(field as string));
      
      // Show a single error message for all required fields
      toast.error(t("fillRequiredFields") || "Please fill in all required fields");
      scrollToFirstError(errorFields);
      return;
    }
    
    // Clear any previous errors if all validation passed
    setErrors([]);
    
    // Execute the registration mutation only if all validation passes
    try {
      const trimmedFormData = Object.fromEntries(
        Object.entries(formData).map(([key, value]) => [key, typeof value === "string" ? value.trim() : value])
      );

      await sellerRegister({
        variables: {
          email: trimmedFormData.email,
          password: trimmedFormData.password,
          shopTitle: trimmedFormData.shopTitle,
          contactNumber: trimmedFormData.contactNumber,
          description: trimmedFormData.description,
          billingAddress: {
            firstname: trimmedFormData.firstname,
            lastname: trimmedFormData.lastname,
            street: [trimmedFormData.street],
            city: trimmedFormData.city,
            postcode: trimmedFormData.postcode,
            countryId: "TN",
            region: {
              region: trimmedFormData.region,
              regionId: trimmedFormData.region_id,
              regionCode: trimmedFormData.region_code,
            },
            vatId: trimmedFormData.vat_id,
          },
        }
      });
    } catch (error: any) {
      // This should not be reached as errors are handled in the mutation hooks
      console.error("Unhandled registration error:", error);
    }
  };

  // Helper function to get input state class
  const getInputStateClass = (field: keyof typeof formData) => {
    if (!formData[field]) return "";
    return validated && !formData[field] ? "is-invalid" : "";
  };

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss={false}
        draggable
        pauseOnHover
        className="toastify-container"
        toastClassName="toastify-toast"
      />
      <div className="wamia-sign-in-form">
      <div className="text-center mb-7">
        <h3 className="text-body-highlight">{t("signUp")}</h3>
        <p className="text-body-tertiary">{t("createYourAccountToday")}</p>
      </div>
      {/* Error alerts removed in favor of toast notifications */}
      <div className="position-relative mt-4">
        <hr className="bg-body-secondary" />
        <div className="divider-content-center">
          {t("mainContactInformations")}
        </div>
      </div>
      <Form ref={formRef} noValidate validated={validated} onSubmit={handleSubmit} className="auth-form">
      <Form.Group className="mb-3 text-start">
          <Form.Label htmlFor="firstname">{t("firstName")} <span className="text-danger">*</span></Form.Label>
          <div className="form-icon-container">
            <Form.Control
              ref={fieldRefs.current.firstname as React.RefObject<HTMLInputElement>}
              id="firstname"
              name="firstname"
              type="text"
              className={`form-icon-input ${getInputStateClass('firstname')}`}
              placeholder={t("firstName")}
              value={formData.firstname}
              onChange={handleInputChange}
              required
            />
            <FontAwesomeIcon
              icon={faUser}
              className="text-body fs-9 form-icon"
            />
            {/* Form feedback removed */}
          </div>
        </Form.Group>
        <Form.Group className="mb-3 text-start">
          <Form.Label htmlFor="lastname">{t("lastName")} <span className="text-danger">*</span></Form.Label>
          <div className="form-icon-container">
            <Form.Control
              ref={fieldRefs.current.lastname as React.RefObject<HTMLInputElement>}
              id="lastname"
              name="lastname"
              type="text"
              className={`form-icon-input ${getInputStateClass('lastname')}`}
              placeholder={t("lastName")}
              value={formData.lastname}
              onChange={handleInputChange}
              required
            />
            <FontAwesomeIcon
              icon={faUser}
              className="text-body fs-9 form-icon"
            />
            {/* Form feedback removed */}
          </div>
        </Form.Group>
        <Form.Group className="mb-3 text-start">
          <Form.Label htmlFor="email">{t("emailAddress")} <span className="text-danger">*</span></Form.Label>
          <div className="form-icon-container">
            <Form.Control
              ref={fieldRefs.current.email as React.RefObject<HTMLInputElement>}
              id="email"
              name="email"
              type="email"
              className={`form-icon-input ${touched.email && !fieldValidation.email.isValid ? 'is-invalid' : ''}`}
              placeholder={t("emailPlaceholder")}
              value={formData.email}
              onChange={handleInputChange}
              onBlur={() => handleBlur('email')}
              required
            />
            <FontAwesomeIcon
              icon={faUser}
              className="text-body fs-9 form-icon"
            />
            {/* Email validation message removed */}
          </div>
        </Form.Group>
        <Form.Group className="mb-3 text-start">
          <Form.Label htmlFor="contactNumber">{t("contactNumber")} <span className="text-danger">*</span></Form.Label>
          <div className="form-icon-container">
            <Form.Control
              ref={fieldRefs.current.contactNumber as React.RefObject<HTMLInputElement>}
              id="contactNumber"
              name="contactNumber"
              type="tel"
              className={`form-icon-input ${touched.contactNumber && !fieldValidation.contactNumber.isValid ? 'is-invalid' : ''}`}
              placeholder={t("contactNumber") || "Phone number (digits only)"}
              value={formData.contactNumber}
              onChange={handleInputChange}
              onKeyDown={handleContactKeyDown}
              onBlur={() => handleBlur('contactNumber')}
              pattern="[0-9+]+"
              inputMode="numeric"
              required
            />
            <FontAwesomeIcon
              icon={faPhone}
              className="text-body fs-9 form-icon"
            />
            {/* Contact number validation message removed */}
          </div>
        </Form.Group>
        <Row className="g-3 mb-3">
          <Col sm={layout === "card" ? 12 : 6} lg={6}>
            <Form.Group>
              <Form.Label htmlFor="password">{t("password")} <span className="text-danger">*</span></Form.Label>
              <div className="form-icon-container">
                <Form.Control
                  ref={fieldRefs.current.password as React.RefObject<HTMLInputElement>}
                  id="password"
                  name="password"
                  type={passwordVisible ? "text" : "password"}
                  className={`form-icon-input ${touched.password && !fieldValidation.password.isValid ? 'is-invalid' : ''}`}
                  placeholder={t("password")}
                  value={formData.password}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur('password')}
                  required
                  minLength={6}
                />
                <FontAwesomeIcon
                  icon={passwordVisible ? faEyeSlash : faEye}
                  className="text-body fs-9 form-icon cursor-pointer"
                  style={{ right: '10px' }}
                  onClick={() => setPasswordVisible(!passwordVisible)}
                  title={passwordVisible ? 'Hide Password' : 'Show Password'}
                />
                {touched.password && formData.password && (
                  <div className={`mt-1 ${!fieldValidation.password.isValid ? 'text-danger' : 'text-muted'}`} style={{fontSize: '0.8rem'}}>
                    <div className={fieldValidation.password.isLongEnough ? 'text-success' : 'text-danger'}>
                      <FontAwesomeIcon icon={fieldValidation.password.isLongEnough ? faCheckCircle : faExclamationCircle} className="me-1" />
                      {"Min 6 characters"}
                    </div>
                    <div className={fieldValidation.password.hasUpperCase ? 'text-success' : 'text-danger'}>
                      <FontAwesomeIcon icon={fieldValidation.password.hasUpperCase ? faCheckCircle : faExclamationCircle} className="me-1" />
                      {"One uppercase letter"}
                    </div>
                    <div className={fieldValidation.password.hasLowerCase ? 'text-success' : 'text-danger'}>
                      <FontAwesomeIcon icon={fieldValidation.password.hasLowerCase ? faCheckCircle : faExclamationCircle} className="me-1" />
                      {"One lowercase letter"}
                    </div>
                    <div className={fieldValidation.password.hasNumber ? 'text-success' : 'text-danger'}>
                      <FontAwesomeIcon icon={fieldValidation.password.hasNumber ? faCheckCircle : faExclamationCircle} className="me-1" />
                      {"One number"}
                    </div>
                    <div className={fieldValidation.password.hasSpecialChar ? 'text-success' : 'text-danger'}>
                      <FontAwesomeIcon icon={fieldValidation.password.hasSpecialChar ? faCheckCircle : faExclamationCircle} className="me-1" />
                      {"One special character"}
                    </div>
                  </div>
                )}
              </div>
            </Form.Group>
          </Col>
          <Col sm={layout === "card" ? 12 : 6} lg={6}>
            <Form.Group>
              <Form.Label htmlFor="confirmPassword">
                {t("confirmPassword")} <span className="text-danger">*</span>
              </Form.Label>
              <div className="form-icon-container">
                <Form.Control
                  ref={fieldRefs.current.confirmPassword as React.RefObject<HTMLInputElement>}
                  id="confirmPassword"
                  name="confirmPassword"
                  type={confirmPasswordVisible ? "text" : "password"}
                  className={`form-icon-input ${touched.confirmPassword && !fieldValidation.confirmPassword.isValid ? 'is-invalid' : ''}`}
                  placeholder={t("confirmPassword")}
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur('confirmPassword')}
                  required
                />
                <FontAwesomeIcon
                  icon={confirmPasswordVisible ? faEyeSlash : faEye}
                  className="text-body fs-9 form-icon cursor-pointer"
                  style={{ right: '10px' }}
                  onClick={() => setConfirmPasswordVisible(!confirmPasswordVisible)}
                  title={confirmPasswordVisible ? 'Hide Password' : 'Show Password'}
                />
                {/* Confirm password validation message removed */}
              </div>
            </Form.Group>
          </Col>
        </Row>
        <div className="position-relative mt-4">
          <hr className="bg-body-secondary" />
          <div className="divider-content-center">{t("storeInformations")}</div>
        </div>
        <Form.Group className="mb-3 text-start">
          <Form.Label htmlFor="shopTitle">{t("shopTitle")} <span className="text-danger">*</span></Form.Label>
          <div className="form-icon-container">
            <Form.Control
              ref={fieldRefs.current.shopTitle as React.RefObject<HTMLInputElement>}
              id="shopTitle"
              name="shopTitle"
              type="text"
              className={`form-icon-input ${getInputStateClass('shopTitle')}`}
              placeholder={t("shopTitle")}
              value={formData.shopTitle}
              onChange={handleInputChange}
              required
            />
            <FontAwesomeIcon
              icon={faStore}
              className="text-body fs-9 form-icon"
            />
            {/* Form feedback removed */}
          </div>
        </Form.Group>
        <Form.Group className="mb-3 text-start">
          <Form.Label htmlFor="company">{t("company")} <span className="text-danger">*</span></Form.Label>
          <div className="form-icon-container">
            <Form.Control
              ref={fieldRefs.current.company as React.RefObject<HTMLInputElement>}
              id="company"
              name="company"
              type="text"
              className={`form-icon-input ${getInputStateClass('company')}`}
              placeholder={t("company")}
              value={formData.company}
              onChange={handleInputChange}
              required
            />
            <FontAwesomeIcon
              icon={faBuilding}
              className="text-body fs-9 form-icon"
            />
            {/* Form feedback removed */}
          </div>
        </Form.Group>
        <Form.Group className="mb-3 text-start">
          <Form.Label htmlFor="description">{t("description")} <span className="text-danger">*</span></Form.Label>
          <div className="form-icon-container">
            <Form.Control
              ref={fieldRefs.current.description as React.RefObject<HTMLTextAreaElement>}
              id="description"
              name="description"
              as="textarea"
              rows={3}
              className={`form-icon-input ${getInputStateClass('description')}`}
              placeholder={t("description")}
              value={formData.description}
              onChange={handleInputChange}
              required
            />
            <FontAwesomeIcon
              icon={faInfoCircle}
              className="text-body fs-9 form-icon"
              style={{ top: '12px' }}
            />
            {/* Form feedback removed */}
          </div>
        </Form.Group>
        <Form.Group className="mb-3 text-start">
          <Form.Label htmlFor="street">{t("streetAddress")} <span className="text-danger">*</span></Form.Label>
          <div className="form-icon-container">
            <Form.Control
              ref={fieldRefs.current.street as React.RefObject<HTMLInputElement>}
              id="street"
              name="street"
              type="text"
              className={`form-icon-input ${getInputStateClass('street')}`}
              placeholder={t("streetAddress")}
              value={formData.street}
              onChange={handleInputChange}
              required
            />
            <FontAwesomeIcon
              icon={faMapMarkedAlt}
              className="text-body fs-9 form-icon"
            />
            {/* Form feedback removed */}
          </div>
        </Form.Group>
        <Form.Group className="mb-3 text-start">
          <Form.Label htmlFor="city">{t("city")} <span className="text-danger">*</span></Form.Label>
          <div className="form-icon-container">
            <Form.Control
              ref={fieldRefs.current.city as React.RefObject<HTMLInputElement>}
              id="city"
              name="city"
              type="text"
              className={`form-icon-input ${getInputStateClass('city')}`}
              placeholder={t("city")}
              value={formData.city}
              onChange={handleInputChange}
              required
            />
            <FontAwesomeIcon
              icon={faCity}
              className="text-body fs-9 form-icon"
            />
            {/* Form feedback removed */}
          </div>
        </Form.Group>
        <Form.Group className="mb-3 text-start">
          <Form.Label htmlFor="postcode">{t("postalCode")} <span className="text-danger">*</span></Form.Label>
          <div className="form-icon-container">
            <Form.Control
              ref={fieldRefs.current.postcode as React.RefObject<HTMLInputElement>}
              id="postcode"
              name="postcode"
              type="text"
              className={`form-icon-input ${getInputStateClass('postcode')}`}
              placeholder={t("postalCode")}
              value={formData.postcode}
              onChange={handleInputChange}
              required
            />
            <FontAwesomeIcon
              icon={faMapPin}
              className="text-body fs-9 form-icon"
            />
            {/* Form feedback removed */}
          </div>
        </Form.Group>
        <Form.Group className="mb-3 text-start">
          <Form.Label htmlFor="region">{t("governorate")} <span className="text-danger">*</span></Form.Label>
          <div className="form-icon-container">
            <Form.Select
              ref={fieldRefs.current.region as React.RefObject<HTMLSelectElement>}
              id="region"
              name="region"
              className={`form-icon-input ${getInputStateClass('region')}`}
              value={formData.region}
              onChange={handleInputChange}
              required
            >
            <option value="Ariana">{t("regionOptions.Ariana")}</option>
            <option value="Béja">{t("regionOptions.Béja")}</option>
            <option value="Ben Arous">{t("regionOptions.Ben Arous")}</option>
            <option value="Bizerte">{t("regionOptions.Bizerte")}</option>
            <option value="Gabès">{t("regionOptions.Gabès")}</option>
            <option value="Gafsa">{t("regionOptions.Gafsa")}</option>
            <option value="Jendouba">{t("regionOptions.Jendouba")}</option>
            <option value="Kairouan">{t("regionOptions.Kairouan")}</option>
            <option value="Kasserine">{t("regionOptions.Kasserine")}</option>
            <option value="Kébili">{t("regionOptions.Kébili")}</option>
            <option value="Kef">{t("regionOptions.Kef")}</option>
            <option value="Mahdia">{t("regionOptions.Mahdia")}</option>
            <option value="Manouba">{t("regionOptions.Manouba")}</option>
            <option value="Medenine">{t("regionOptions.Medenine")}</option>
            <option value="Monastir">{t("regionOptions.Monastir")}</option>
            <option value="Nabeul">{t("regionOptions.Nabeul")}</option>
            <option value="Sfax">{t("regionOptions.Sfax")}</option>
            <option value="Sidi Bouzid">
              {t("regionOptions.Sidi Bouzid")}
            </option>
            <option value="Siliana">{t("regionOptions.Siliana")}</option>
            <option value="Sousse">{t("regionOptions.Sousse")}</option>
            <option value="Tataouine">{t("regionOptions.Tataouine")}</option>
            <option value="Tozeur">{t("regionOptions.Tozeur")}</option>
            <option value="Tunis">{t("regionOptions.Tunis")}</option>
            <option value="Zaghouan">{t("regionOptions.Zaghouan")}</option>
            {/* Add other options */}
            </Form.Select>
            <FontAwesomeIcon
              icon={faMapMarkedAlt}
              className="text-body fs-9 form-icon"
            />
          </div>
        </Form.Group>
        <Form.Group className="mb-3 text-start">
          <Form.Label htmlFor="vat_id">{t("vatId")} <span className="text-danger">*</span></Form.Label>
          <div className="form-icon-container">
            <Form.Control
              ref={fieldRefs.current.vat_id as React.RefObject<HTMLInputElement>}
              id="vat_id"
              name="vat_id"
              type="text"
              className={`form-icon-input ${getInputStateClass('vat_id')}`}
              placeholder={t("vatId")}
              value={formData.vat_id}
              onChange={handleInputChange}
              required
            />
            <FontAwesomeIcon
              icon={faInfoCircle}
              className="text-body fs-9 form-icon"
            />
            {/* Form feedback removed */}
          </div>
        </Form.Group>
        <Row className="mb-3">
          <Col>
            <Form.Check type="checkbox" required>
              <Form.Check.Input
                type="checkbox"
                name="termsService"
                id="termsService"
                checked={termsAgreed}
                onChange={e => {
                  setTermsAgreed(e.target.checked);
                  setTouched(prev => ({
                    ...prev,
                    termsService: true
                  }));
                }}
                required
              />
               <Form.Check.Label
                htmlFor="termsService"
                className="mb-0 text-body"
              >
                {t("termsService")} <Link to="#!" style={{color: "#0d6efd"}}>{t("terms")}</Link> <span className="text-body">{t("and")} </span>
                <Link to="#!" style={{color: "#0d6efd"}}>{t("privacyPolicy")}</Link>
              </Form.Check.Label>
              {validated && !termsAgreed && (
                <div className="invalid-feedback d-block">
                  {t("termsRequired") || "You must agree to the terms and privacy policy"}
                </div>
              )}
            </Form.Check>
          </Col>
        </Row>
        <Button
          variant="warning"
          className="w-100 mb-3"
          type="submit"
        >
          {loading ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
                style={{ width: '0.8rem', height: '0.8rem' }}
              />
              {t("signingUp")}
            </>
          ) : (
            t("signUpButton")
          )}
        </Button>
        <div className="text-center">
          <Link
            to="/auth/login"
            className="fs-9 fw-bold"
          >
            {t("signInToExistingAccount")}
          </Link>
        </div>
      </Form>
    </div>
    </>
  );
};

export default SignUpForm;
